import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import { setActiveFooterTab } from "../../store/actions";
import { setBets } from "../../store/Betslip/actions";
import { axiosApi } from "../../helpers/repository";

/**
 * RN booking-code bottom sheet (ported from betyakata-mobile CustomTabBar /
 * SlipBar). Shown when the bet slip is opened while empty.
 */
function BookingCodeSheet({ isOpen, onClose, history }) {
  const dispatch = useDispatch();
  const [bookingCode, setBookingCode] = useState("");
  const [bookingError, setBookingError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const close = () => {
    if (isLoading) return;
    setBookingError("");
    setBookingCode("");
    onClose();
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.trim()) {
        setBookingCode(text.trim().toUpperCase());
        if (bookingError) setBookingError("");
      }
    } catch (e) {
      // Clipboard permission denied — user can still type the code
    }
  };

  const handleLoad = async () => {
    const trimmed = bookingCode.trim().toUpperCase();
    if (!trimmed) {
      setBookingError("Please enter a booking code.");
      return;
    }
    setBookingError("");
    setIsLoading(true);
    try {
      const response = await axiosApi.get(
        `/client/get-booked-bet/${encodeURIComponent(trimmed)}`,
      );
      const bookedBets = response?.data?.data;
      if (!Array.isArray(bookedBets) || bookedBets.length === 0) {
        setBookingError(response?.data?.data || "Invalid booking code.");
        return;
      }
      dispatch(setBets(bookedBets));
      setBookingCode("");
      onClose();
      dispatch(setActiveFooterTab("betslip"));
      history.push("/betSlip");
    } catch (error) {
      setBookingError(
        error?.response?.data?.data ||
          error?.response?.data?.message ||
          "Unable to load booking code.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const actionButton = (filled, disabled) => ({
    flex: 1,
    height: 48,
    borderRadius: 8,
    cursor: disabled ? "default" : "pointer",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    border: filled ? "1px solid #e0cc00" : "1px solid #2c3752",
    background: filled ? "#FFED00" : "#1e2a3a",
    color: filled ? "#111" : "#C8D2EA",
    opacity: filled && disabled ? 0.6 : 1,
  });

  return (
    <div className="fixed inset-0 z-[1100] lg:hidden">
      <div
        className="absolute inset-0 bg-[rgba(4,9,20,0.68)]"
        onClick={close}
      />
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: 12 }}>
        <div
          data-testid="booking-sheet-card"
          style={{
            background: "#1a2235",
            border: "1px solid #2c3752",
            borderRadius: 20,
            padding: 16,
            width: "100%",
            maxWidth: 480,
            margin: "0 auto",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div
              style={{
                width: 30, height: 30, borderRadius: 8, background: "#1e2a3a",
                border: "1px solid #2c3752", display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 13, flexShrink: 0,
              }}
            >
              🎫
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 18, lineHeight: "22px" }}>
                Load Booking Code
              </div>
              <div style={{ color: "#8b8fa8", fontSize: 12, lineHeight: "16px", marginTop: 2 }}>
                Paste a code to load every selection into your slip.
              </div>
            </div>
            <button
              onClick={close}
              disabled={isLoading}
              aria-label="Close"
              style={{
                width: 32, height: 32, borderRadius: 16, flexShrink: 0, border: "none",
                background: "#1e2a3a", color: "#C8D2EA", fontSize: 22, lineHeight: "24px", cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <div style={{ color: "#8b8fa8", fontSize: 10, fontWeight: 600, letterSpacing: "0.8px", marginTop: 2 }}>
            BOOKING CODE
          </div>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <input
              style={{
                width: "100%", height: 50, boxSizing: "border-box", background: "#0f1923",
                color: "#fff", borderRadius: 8, fontSize: 14, fontWeight: 600, outline: "none",
                padding: "0 92px 0 14px", textTransform: "uppercase",
                border: `1px solid ${bookingError ? "#ef4444" : "#2c3752"}`,
              }}
              placeholder="Paste booking code"
              value={bookingCode}
              disabled={isLoading}
              onChange={(e) => {
                setBookingCode(e.target.value);
                if (bookingError) setBookingError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleLoad();
              }}
            />
            <button
              onClick={handlePaste}
              disabled={isLoading}
              style={{
                position: "absolute", right: 6, top: 0, bottom: 0, padding: "0 8px",
                display: "flex", alignItems: "center", gap: 6, background: "none",
                border: "none", cursor: "pointer", color: "#F5C518", fontSize: 13, fontWeight: 700,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 4H18C19.1 4 20 4.9 20 6V19C20 20.1 19.1 21 18 21H6C4.9 21 4 20.1 4 19V6C4 4.9 4.9 4 6 4H9"
                  stroke="#F5C518"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 3H10C9.44772 3 9 3.44772 9 4V6C9 6.55228 9.44772 7 10 7H14C14.5523 7 15 6.55228 15 6V4C15 3.44772 14.5523 3 14 3Z"
                  stroke="#F5C518"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Paste
            </button>
          </div>

          {!!bookingError && (
            <div style={{ color: "#ef4444", fontSize: 12 }}>{bookingError}</div>
          )}

          <div style={{ color: "#6f7f9d", fontSize: 12, minHeight: 16 }}>
            6-10 characters · letters &amp; numbers
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={close} disabled={isLoading} style={actionButton(false, isLoading)}>
              Cancel
            </button>
            <button onClick={handleLoad} disabled={isLoading} style={actionButton(true, isLoading)}>
              {isLoading ? "Loading..." : "Load Bet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withRouter(BookingCodeSheet);
